import React from "react";
import { AppBar, Tab, Tabs as MuiTabs } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import EnumUtils from "../utils/EnumUtils";
import StaffRole from "../types/enum/StaffRole";
import { PlanType } from "types/enum/PlanType";

function a11yProps(index: any) {
  return {
    id: `nav-tab-${index}`,
    "aria-controls": `nav-tabpanel-${index}`,
  };
}

interface LinkTabProps {
  label?: string;
  value: string;
}

function LinkTab(props: LinkTabProps) {
  return (
    <Tab
      onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
      }}
      {...props}
    />
  );
}

const Tabs = () => {
  const { t } = useTranslation("layout");
  const history = useHistory();
  const role = useSelector((state) => state.account.staff.role);
  const plan = useSelector(
    (state) => state.account.staff.company.companyGroup.plan
  );
  // 業態 (来店型 / デリバリー型)。companyGroup 単位で 1 業態に固定 (混在不可)。
  // super admin (notelMaster 等) は「ログイン企業切り替え」 で staff.companyId を変える。
  // state.company (全社一覧) から current company を引いて、その companyGroup.businessType を見る。
  // company マスターから引けない場合は login 時 staff.company.companyGroup.businessType を fallback。
  const currentCompanyId = useSelector(
    (state) => state.account.staff.companyId
  );
  const companies = useSelector((state) => state.company);
  const loginCompanyBusinessType = useSelector(
    (state) => state.account.staff.company.companyGroup.businessType
  );
  const currentCompany = companies?.find(
    (c) => c.companyId === currentCompanyId
  );
  const businessType =
    currentCompany?.companyGroup?.businessType ?? loginCompanyBusinessType;
  const isVisit = businessType === "visit";
  const handleChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    history.push(newValue);
  };
  let links = [
    { label: t("tabs.top"), value: "/", sort: 1 },
    { label: t("tabs.ai_support"), value: "/aiSupport", sort: 2 },
    { label: t("tabs.order"), value: "/orderList", sort: 4 },
    { label: t("tabs.cast_shift"), value: "/castShift", sort: 5 },
    { label: t("tabs.cast_schedule"), value: "/castSchedule", sort: 7 },
  ];
  if (EnumUtils.mapToEnum(PlanType, plan) !== PlanType.free) {
    links = [
      ...links,
      { label: t("tabs.board"), value: "/messageBoard", sort: 3 },
    ];
  }
  const hasManagerRole = [
    StaffRole.notelMaster,
    StaffRole.notelCallCenterEmployee,
    StaffRole.notelCallCenterPartTime,
    StaffRole.clientMaster,
    StaffRole.clientShopMaster,
  ].includes(
    EnumUtils.mapToEnum(StaffRole, role) || StaffRole.clientShopPartTime
  );
  if (hasManagerRole) {
    // 分析タブは business_type を問わず表示 (visit でも分析は必要)。
    links = [...links, { label: t("tabs.analytics"), value: "/analytics", sort: 12 }];
    // 配車/ドライバーシフトは来店型 (visit) では非表示。visit は既存キャストスケジュール
    // 画面側で部屋軸の表示に切り替える (Phase 2.3)。
    if (!isVisit) {
      links = [
        ...links,
        [
          { label: t("tabs.driver_shift"), value: "/driverShift", sort: 6 },
          { label: t("tabs.dispatch"), value: "/driverSchedule", sort: 8 },
        ],
      ].flat();
    }
  }
  if (EnumUtils.mapToEnum(StaffRole, role) !== StaffRole.clientShopPartTime) {
    links = [
      ...links,
      [
        { label: t("tabs.settlement"), value: "/payoff", sort: 11 },
        { label: t("tabs.master"), value: "/setting", sort: 13 },
      ],
    ].flat();
  }

  return (
    <div style={{ flexGrow: 1, width: "100vw" }}>
      <AppBar position="static">
        <MuiTabs
          value={history.location.pathname}
          onChange={handleChange}
          aria-label="nav tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          {links
            .sort((a, b) => {
              if (a.sort < b.sort) return -1;
              if (a.sort > b.sort) return 1;
              return 0;
            })
            .map((link, index) => (
              <LinkTab
                label={link.label}
                value={link.value}
                key={index}
                {...a11yProps(index)}
              />
            ))}
        </MuiTabs>
      </AppBar>
    </div>
  );
};

export default Tabs;
